import { z } from 'zod';
import { uuidSchema } from './common.js';
import { validateNumberPattern } from './number-format.js';
const numberPattern = z.string().refine((v) => validateNumberPattern(v) === null, {
    message: 'Invalid number pattern, e.g. ADM-{YYYY}-{SEQ:5}',
});
/**
 * Registry of configuration keys. Only keys listed here can be stored; every value is validated
 * against its schema. Later phases add their keys (grading, fees, certificates, ...).
 */
export const SETTINGS_REGISTRY = {
    'numbering.admission': {
        label: 'Admission number format',
        description: 'Tokens: {YYYY} {YY} {MM} {DD} and {SEQ:n}',
        group: 'Number formats',
        kind: 'string',
        scope: 'both',
        schema: numberPattern,
        default: 'ADM-{YYYY}-{SEQ:5}',
    },
    'numbering.receipt': {
        label: 'Fee receipt number format',
        description: 'Tokens: {YYYY} {YY} {MM} {DD} and {SEQ:n}',
        group: 'Number formats',
        kind: 'string',
        scope: 'both',
        schema: numberPattern,
        default: 'RCT-{YYYY}{MM}-{SEQ:6}',
    },
    'numbering.employee': {
        label: 'Employee number format',
        description: 'Tokens: {YYYY} {YY} {MM} {DD} and {SEQ:n}',
        group: 'Number formats',
        kind: 'string',
        scope: 'both',
        schema: numberPattern,
        default: 'EMP-{SEQ:5}',
    },
    'numbering.enquiry': {
        label: 'Enquiry number format',
        description: 'Tokens: {YYYY} {YY} {MM} {DD} and {SEQ:n}',
        group: 'Number formats',
        kind: 'string',
        scope: 'both',
        schema: numberPattern,
        default: 'ENQ-{YYYY}-{SEQ:5}',
    },
    'numbering.application': {
        label: 'Application number format',
        description: 'Tokens: {YYYY} {YY} {MM} {DD} and {SEQ:n}',
        group: 'Number formats',
        kind: 'string',
        scope: 'both',
        schema: numberPattern,
        default: 'APP-{YYYY}-{SEQ:5}',
    },
    'numbering.invoice': {
        label: 'Fee invoice number format',
        description: 'Tokens: {YYYY} {YY} {MM} {DD} and {SEQ:n}',
        group: 'Number formats',
        kind: 'string',
        scope: 'both',
        schema: numberPattern,
        default: 'INV-{YYYY}-{SEQ:6}',
    },
    'numbering.journal': {
        label: 'Journal entry number format',
        description: 'Tokens: {YYYY} {YY} {MM} {DD} and {SEQ:n}',
        group: 'Number formats',
        kind: 'string',
        scope: 'both',
        schema: numberPattern,
        default: 'JV-{YYYY}-{SEQ:6}',
    },
    'numbering.accession': {
        label: 'Library accession number format',
        description: 'Tokens: {YYYY} {YY} {MM} {DD} and {SEQ:n}',
        group: 'Number formats',
        kind: 'string',
        scope: 'both',
        schema: numberPattern,
        default: 'ACC-{SEQ:6}',
    },
    'numbering.asset': {
        label: 'Asset tag format',
        description: 'Tokens: {YYYY} {YY} {MM} {DD} and {SEQ:n}',
        group: 'Number formats',
        kind: 'string',
        scope: 'both',
        schema: numberPattern,
        default: 'AST-{SEQ:5}',
    },
    'numbering.certificate': {
        label: 'Certificate number format',
        description: 'Tokens: {YYYY} {YY} {MM} {DD} and {SEQ:n}',
        group: 'Number formats',
        kind: 'string',
        scope: 'both',
        schema: numberPattern,
        default: 'CERT-{YYYY}-{SEQ:5}',
    },
    'library.loan_days_student': {
        label: 'Loan period for students (days)',
        description: 'How long a student may keep a book',
        group: 'Library',
        kind: 'number',
        scope: 'both',
        schema: z.number().int().min(1).max(180),
        default: 14,
    },
    'library.loan_days_staff': {
        label: 'Loan period for staff (days)',
        description: 'How long a staff member may keep a book',
        group: 'Library',
        kind: 'number',
        scope: 'both',
        schema: z.number().int().min(1).max(365),
        default: 30,
    },
    'library.max_books_student': {
        label: 'Books per student',
        description: 'Most books a student may hold at once',
        group: 'Library',
        kind: 'number',
        scope: 'both',
        schema: z.number().int().min(1).max(50),
        default: 3,
    },
    'library.max_books_staff': {
        label: 'Books per staff member',
        description: 'Most books a staff member may hold at once',
        group: 'Library',
        kind: 'number',
        scope: 'both',
        schema: z.number().int().min(1).max(100),
        default: 5,
    },
    'library.max_renewals': {
        label: 'Renewals per loan',
        description: 'How many times a loan may be renewed',
        group: 'Library',
        kind: 'number',
        scope: 'both',
        schema: z.number().int().min(0).max(20),
        default: 2,
    },
    'library.fine_per_day_minor': {
        label: 'Overdue fine per day (minor units)',
        description: 'Charged for each day a book is late, in the smallest currency unit',
        group: 'Library',
        kind: 'number',
        scope: 'both',
        schema: z.number().int().min(0).max(1000000),
        default: 50,
    },
    'attendance.working_days': {
        label: 'Working days',
        description: 'Days of the week the school is open (0 = Sunday ... 6 = Saturday)',
        group: 'Attendance',
        kind: 'weekdays',
        scope: 'both',
        schema: z
            .array(z.number().int().min(0).max(6))
            .min(1)
            .max(7)
            .refine((days) => new Set(days).size === days.length, 'Days must be unique'),
        default: [1, 2, 3, 4, 5],
    },
    'attendance.late_after_minutes': {
        label: 'Mark late after (minutes)',
        description: 'Students arriving later than this are marked Late',
        group: 'Attendance',
        kind: 'number',
        scope: 'both',
        schema: z.number().int().min(0).max(240),
        default: 15,
    },
    'attendance.edit_window_days': {
        label: 'Attendance edit window (days)',
        description: 'How many days back a register can still be marked or edited without the "correct attendance" permission',
        group: 'Attendance',
        kind: 'number',
        scope: 'both',
        schema: z.number().int().min(0).max(30),
        default: 1,
    },
    'fees.late_fee_grace_days': {
        label: 'Late fee grace period (days)',
        description: 'Days after the due date before late fees apply',
        group: 'Fees',
        kind: 'number',
        scope: 'both',
        schema: z.number().int().min(0).max(90),
        default: 0,
    },
    'branding.display_name': {
        label: 'Display name',
        description: 'Name shown in the application header (blank = school/organization name)',
        group: 'Branding',
        kind: 'string',
        scope: 'both',
        schema: z.string().trim().max(100),
        default: '',
    },
    'branding.primary_color': {
        label: 'Primary colour',
        description: 'Hex colour, e.g. #1e3a8a',
        group: 'Branding',
        kind: 'string',
        scope: 'both',
        schema: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a 6-digit hex colour like #1e3a8a'),
        default: '#1e3a8a',
    },
    'notifications.email_enabled': {
        label: 'Email notifications',
        description: 'Send notifications by email',
        group: 'Notifications',
        kind: 'boolean',
        scope: 'both',
        schema: z.boolean(),
        default: true,
    },
    'notifications.sms_enabled': {
        label: 'SMS notifications',
        description: 'Send notifications by SMS (requires an SMS provider)',
        group: 'Notifications',
        kind: 'boolean',
        scope: 'both',
        schema: z.boolean(),
        default: false,
    },
};
export const isSettingKey = (key) => Object.hasOwn(SETTINGS_REGISTRY, key);
export const settingKeySchema = z.string().refine(isSettingKey, 'Unknown setting key');
export const updateSettingSchema = z.object({
    value: z.unknown(),
    /** Omit for an organization-level value. */
    schoolId: uuidSchema.nullable().optional(),
    /** Platform administrators must say which organization; ignored for everyone else. */
    organizationId: uuidSchema.optional(),
});
export const listSettingsQuerySchema = z.object({
    schoolId: uuidSchema.optional(),
    organizationId: uuidSchema.optional(),
});
//# sourceMappingURL=settings.js.map